// Gestão de Parcerias
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { 
  Plus, Edit, Trash2, Handshake, Settings, 
  CheckCircle, XCircle, AlertCircle, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const tiposEntidade = [
  'Ginásio', 'Farmácia', 'Posto de Combustível', 'Sapataria', 
  'Óptica', 'Clínica', 'Janeira', 'Outro'
];

const GestaoParcerias = ({ user }) => {
  const [parcerias, setParcerias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  // Modo avançado — esconde acções destrutivas (Eliminar parceria) por defeito
  const [showAvancado, setShowAvancado] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    nome_entidade: '',
    tipo_parceria: 'empresa',
    tipo_entidade: 'Outro',
    objetivo_parceria: '',
    modelo_parceria: 'Contínuo',
    data_inicio_campanha: '',
    data_fim_campanha: '',
    dias_validade_padrao: 8,
    regra_acumulacao: 'Não acumula',
    limite_maximo_vales: '',
    bloqueia_se_vale_ativo: false,
    limite_um_vale_por_dia: false,
    alerta_colaborador: true,
    data_inicio_parceria: '',
    data_fim_parceria: '',
    regras_desconto: [],
    regras_parceiro_janeira: [],
    ativar_parceiro_janeira: false,
    // Configuração de partilha de custos
    config_custos: {
      tipo_custo: 'unilateral_janeira',
      janeira_parceiro_percentagem_janeira: 100,
      janeira_parceiro_percentagem_parceiro: 0,
      janeira_parceiro_valor_fixo_janeira: null,
      janeira_parceiro_valor_fixo_parceiro: null,
      parceiro_janeira_percentagem_janeira: 0,
      parceiro_janeira_percentagem_parceiro: 100,
      parceiro_janeira_valor_fixo_janeira: null,
      parceiro_janeira_valor_fixo_parceiro: null
    }
  });

  // Regra de desconto temporária - agora com direção
  const [novaRegraIda, setNovaRegraIda] = useState({
    valor_minimo_compra: '',
    tipo_beneficio: 'Valor Fixo',
    valor_beneficio: '',
    onde_usado: '',
    quem_suporta: 'Janeira',
    limite_litros: '',
    valor_minimo_utilizacao: '',
    data_inicio: '',
    data_fim: ''
  });

  const [novaRegraVolta, setNovaRegraVolta] = useState({
    valor_minimo_compra: '',
    tipo_beneficio: 'Valor Fixo',
    valor_beneficio: '',
    onde_usado: 'Janeira',
    quem_suporta: '',
    limite_litros: '',
    valor_minimo_utilizacao: '',
    data_inicio: '',
    data_fim: ''
  });

  useEffect(() => {
    loadParcerias();
  }, []);

  const loadParcerias = async () => {
    try {
      const response = await api.get('/parcerias/entidades');
      setParcerias(response.data);
    } catch (error) {
      console.error('Erro ao carregar parcerias:', error);
      toast.error('Erro ao carregar parcerias');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (parceria = null) => {
    const defaultConfigCustos = {
      tipo_custo: 'unilateral_janeira',
      janeira_parceiro_percentagem_janeira: 100,
      janeira_parceiro_percentagem_parceiro: 0,
      janeira_parceiro_valor_fixo_janeira: null,
      janeira_parceiro_valor_fixo_parceiro: null,
      parceiro_janeira_percentagem_janeira: 0,
      parceiro_janeira_percentagem_parceiro: 100,
      parceiro_janeira_valor_fixo_janeira: null,
      parceiro_janeira_valor_fixo_parceiro: null
    };

    if (parceria) {
      setEditando(parceria);
      setFormData({
        nome_entidade: parceria.nome_entidade || '',
        tipo_parceria: parceria.tipo_parceria || 'empresa',
        tipo_entidade: parceria.tipo_entidade || 'Outro',
        objetivo_parceria: parceria.objetivo_parceria || '',
        modelo_parceria: parceria.modelo_parceria || 'Contínuo',
        data_inicio_campanha: parceria.data_inicio_campanha?.split('T')[0] || '',
        data_fim_campanha: parceria.data_fim_campanha?.split('T')[0] || '',
        dias_validade_padrao: parceria.dias_validade_padrao || 8,
        regra_acumulacao: parceria.regra_acumulacao || 'Não acumula',
        limite_maximo_vales: parceria.limite_maximo_vales || '',
        bloqueia_se_vale_ativo: parceria.bloqueia_se_vale_ativo || false,
        limite_um_vale_por_dia: parceria.limite_um_vale_por_dia || false,
        alerta_colaborador: parceria.alerta_colaborador ?? true,
        data_inicio_parceria: parceria.data_inicio_parceria?.split('T')[0] || '',
        data_fim_parceria: parceria.data_fim_parceria?.split('T')[0] || '',
        regras_desconto: parceria.regras_desconto || [],
        regras_parceiro_janeira: parceria.regras_parceiro_janeira || [],
        ativar_parceiro_janeira: parceria.ativar_parceiro_janeira || (parceria.regras_parceiro_janeira?.length > 0) || false,
        config_custos: parceria.config_custos || defaultConfigCustos
      });
    } else {
      setEditando(null);
      setFormData({
        nome_entidade: '',
        tipo_parceria: 'empresa',
        tipo_entidade: 'Outro',
        objetivo_parceria: '',
        modelo_parceria: 'Contínuo',
        data_inicio_campanha: '',
        data_fim_campanha: '',
        dias_validade_padrao: 8,
        regra_acumulacao: 'Não acumula',
        limite_maximo_vales: '',
        bloqueia_se_vale_ativo: false,
        limite_um_vale_por_dia: false,
        alerta_colaborador: true,
        data_inicio_parceria: new Date().toISOString().split('T')[0],
        data_fim_parceria: '',
        regras_desconto: [],
        regras_parceiro_janeira: [],
        ativar_parceiro_janeira: false,
        config_custos: defaultConfigCustos
      });
    }
    setShowModal(true);
    const nomeParceria = parceria ? parceria.nome_entidade : '';
    setNovaRegraIda({
      valor_minimo_compra: '', tipo_beneficio: 'Valor Fixo', valor_beneficio: '',
      onde_usado: nomeParceria, quem_suporta: 'Janeira', limite_litros: '',
      valor_minimo_utilizacao: ''
    });
    setNovaRegraVolta({
      valor_minimo_compra: '', tipo_beneficio: 'Valor Fixo', valor_beneficio: '',
      onde_usado: 'Janeira', quem_suporta: nomeParceria, limite_litros: '',
      valor_minimo_utilizacao: ''
    });
  };

  const handleAddRegra = (direcao) => {
    const novaRegra = direcao === 'ida' ? novaRegraIda : novaRegraVolta;
    const campo = direcao === 'ida' ? 'regras_desconto' : 'regras_parceiro_janeira';
    
    if (!novaRegra.valor_minimo_compra || !novaRegra.valor_beneficio) {
      toast.error('Preencha os valores da regra de desconto');
      return;
    }
    
    const regra = {
      valor_minimo_compra: parseFloat(novaRegra.valor_minimo_compra),
      tipo_beneficio: novaRegra.tipo_beneficio,
      valor_beneficio: parseFloat(novaRegra.valor_beneficio),
      onde_usado: novaRegra.onde_usado || (direcao === 'ida' ? formData.nome_entidade : 'Janeira'),
      quem_suporta: novaRegra.quem_suporta || 'Janeira'
    };
    if (novaRegra.tipo_beneficio === '€/Litro' && novaRegra.limite_litros) {
      regra.limite_litros = parseFloat(novaRegra.limite_litros);
    }
    if (novaRegra.valor_minimo_utilizacao) {
      regra.valor_minimo_utilizacao = parseFloat(novaRegra.valor_minimo_utilizacao);
    }
    if (novaRegra.data_inicio) {
      regra.data_inicio = novaRegra.data_inicio;
    }
    if (novaRegra.data_fim) {
      regra.data_fim = novaRegra.data_fim;
    }

    setFormData(prev => ({
      ...prev,
      [campo]: [...prev[campo], regra]
    }));
    
    if (direcao === 'ida') {
      setNovaRegraIda({
        valor_minimo_compra: '', tipo_beneficio: 'Valor Fixo', valor_beneficio: '',
        onde_usado: formData.nome_entidade, quem_suporta: 'Janeira', limite_litros: '',
        valor_minimo_utilizacao: '', data_inicio: '', data_fim: ''
      });
    } else {
      setNovaRegraVolta({
        valor_minimo_compra: '', tipo_beneficio: 'Valor Fixo', valor_beneficio: '',
        onde_usado: 'Janeira', quem_suporta: formData.nome_entidade, limite_litros: '',
        valor_minimo_utilizacao: '', data_inicio: '', data_fim: ''
      });
    }
  };

  const handleRemoveRegra = (campo, index) => {
    setFormData(prev => ({
      ...prev,
      [campo]: prev[campo].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nome_entidade.trim()) {
      toast.error('Introduza o nome da entidade');
      return;
    }
    if (!formData.data_inicio_parceria) {
      toast.error('Introduza a data de início');
      return;
    }

    // Auto-adicionar regras preenchidas mas não clicadas em "Adicionar"
    const regrasIda = [...formData.regras_desconto];
    if (novaRegraIda.valor_minimo_compra && novaRegraIda.valor_beneficio) {
      const regraExtra = {
        valor_minimo_compra: parseFloat(novaRegraIda.valor_minimo_compra),
        tipo_beneficio: novaRegraIda.tipo_beneficio,
        valor_beneficio: parseFloat(novaRegraIda.valor_beneficio),
        onde_usado: novaRegraIda.onde_usado || formData.nome_entidade,
        quem_suporta: novaRegraIda.quem_suporta || 'Janeira'
      };
      if (novaRegraIda.tipo_beneficio === '€/Litro' && novaRegraIda.limite_litros) {
        regraExtra.limite_litros = parseFloat(novaRegraIda.limite_litros);
      }
      if (novaRegraIda.valor_minimo_utilizacao) {
        regraExtra.valor_minimo_utilizacao = parseFloat(novaRegraIda.valor_minimo_utilizacao);
      }
      regrasIda.push(regraExtra);
    }

    let regrasVolta = formData.ativar_parceiro_janeira ? [...formData.regras_parceiro_janeira] : [];
    if (formData.ativar_parceiro_janeira && novaRegraVolta.valor_minimo_compra && novaRegraVolta.valor_beneficio) {
      const regraExtra = {
        valor_minimo_compra: parseFloat(novaRegraVolta.valor_minimo_compra),
        tipo_beneficio: novaRegraVolta.tipo_beneficio,
        valor_beneficio: parseFloat(novaRegraVolta.valor_beneficio),
        onde_usado: novaRegraVolta.onde_usado || 'Janeira',
        quem_suporta: novaRegraVolta.quem_suporta || formData.nome_entidade
      };
      if (novaRegraVolta.tipo_beneficio === '€/Litro' && novaRegraVolta.limite_litros) {
        regraExtra.limite_litros = parseFloat(novaRegraVolta.limite_litros);
      }
      if (novaRegraVolta.valor_minimo_utilizacao) {
        regraExtra.valor_minimo_utilizacao = parseFloat(novaRegraVolta.valor_minimo_utilizacao);
      }
      regrasVolta.push(regraExtra);
    }

    if (regrasIda.length === 0) {
      toast.error('Adicione pelo menos uma regra de desconto (Janeira → Parceiro)');
      return;
    }

    if (formData.ativar_parceiro_janeira && regrasVolta.length === 0) {
      toast.error('Adicione pelo menos uma regra para Parceiro → Janeira, ou desative esta direção');
      return;
    }

    try {
      const payload = {
        nome_entidade: formData.nome_entidade,
        tipo_parceria: formData.tipo_parceria,
        tipo_entidade: formData.tipo_entidade,
        modelo_parceria: formData.modelo_parceria,
        dias_validade_padrao: formData.dias_validade_padrao,
        regra_acumulacao: formData.regra_acumulacao,
        bloqueia_se_vale_ativo: formData.bloqueia_se_vale_ativo,
        limite_um_vale_por_dia: formData.limite_um_vale_por_dia,
        alerta_colaborador: formData.alerta_colaborador,
        data_inicio_parceria: new Date(formData.data_inicio_parceria).toISOString(),
        regras_desconto: regrasIda,
        regras_parceiro_janeira: regrasVolta,
        ativar_parceiro_janeira: formData.ativar_parceiro_janeira || false,
        config_custos: formData.config_custos
      };

      // Campos opcionais - só enviar se tiverem valor
      if (formData.objetivo_parceria) payload.objetivo_parceria = formData.objetivo_parceria;
      if (formData.data_fim_parceria) payload.data_fim_parceria = new Date(formData.data_fim_parceria).toISOString();
      if (formData.data_inicio_campanha) payload.data_inicio_campanha = new Date(formData.data_inicio_campanha).toISOString();
      if (formData.data_fim_campanha) payload.data_fim_campanha = new Date(formData.data_fim_campanha).toISOString();
      if (formData.limite_maximo_vales) payload.limite_maximo_vales = parseInt(formData.limite_maximo_vales);

      if (editando) {
        await api.put(`/parcerias/entidades/${editando.id}`, payload);
        toast.success('Parceria atualizada com sucesso');
      } else {
        await api.post('/parcerias/entidades', payload);
        toast.success('Parceria criada com sucesso');
      }

      setShowModal(false);
      loadParcerias();
    } catch (error) {
      console.error('Erro ao guardar parceria:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join(', '));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Erro ao guardar parceria');
      }
    }
  };

  const handleToggleEstado = async (parceria) => {
    try {
      await api.put(`/parcerias/entidades/${parceria.id}`, {
        estado: !parceria.estado
      });
      toast.success(`Parceria ${parceria.estado ? 'desativada' : 'ativada'}`);
      loadParcerias();
    } catch (error) {
      toast.error('Erro ao alterar estado');
    }
  };

  const criarCampanhaVales = (parceria) => {
    // Guarda intent no localStorage e navega para Fidelizacao -> Campanhas
    try {
      localStorage.setItem('janeira_pending_campanha', JSON.stringify({
        tipo: 'promocional',
        parceria_id: parceria.id,
        parceria_nome: parceria.nome_entidade,
        created_at: Date.now()
      }));
    } catch (e) { /* ignore */ }
    window.dispatchEvent(new CustomEvent('janeira:navigate', { detail: { target: 'fidelizacao-campanhas' } }));
    toast.success(`A abrir nova campanha de vales para ${parceria.nome_entidade}...`);
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    
    try {
      await api.delete(`/parcerias/entidades/${showDeleteConfirm.id}`);
      toast.success('Parceria eliminada com sucesso');
      setShowDeleteConfirm(null);
      loadParcerias();
    } catch (error) {
      toast.error('Erro ao eliminar parceria');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gestao-parcerias">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Gestão de Parcerias</h2>
          <p className="text-sm text-muted-foreground">
            Configure as entidades parceiras e regras de desconto
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowAvancado(v => !v)}
            variant="outline"
            size="sm"
            data-testid="parcerias-toggle-avancado"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            title="Mostrar/esconder acções avançadas (eliminar parceria)"
          >
            {showAvancado ? 'Esconder Avançado' : 'Avançado'}
          </Button>
          <Button onClick={() => handleOpenModal()} data-testid="btn-nova-parceria">
            <Plus className="h-4 w-4 mr-2" />
            Nova Parceria
          </Button>
        </div>
      </div>

      {/* Lista de Parcerias */}
      <Card>
        <CardContent className="pt-6">
          {parcerias.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Handshake className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma parceria configurada</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => handleOpenModal()}
              >
                Criar primeira parceria
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parcerias.map(parceria => (
                  <TableRow key={parceria.id}>
                    <TableCell className="font-medium">{parceria.nome_entidade}</TableCell>
                    <TableCell>
                      <Badge variant={parceria.tipo_parceria === 'perfil_beneficio' ? 'default' : 'outline'}>
                        {parceria.tipo_parceria === 'perfil_beneficio' ? 'Perfil' : (parceria.tipo_entidade || 'Empresa')}
                      </Badge>
                    </TableCell>
                    <TableCell>{parceria.modelo_parceria}</TableCell>
                    <TableCell>{parceria.dias_validade_padrao} dias</TableCell>
                    <TableCell>
                      <Badge variant={parceria.estado ? 'success' : 'secondary'}>
                        {parceria.estado ? (
                          <><CheckCircle className="h-3 w-3 mr-1" />Ativa</>
                        ) : (
                          <><XCircle className="h-3 w-3 mr-1" />Inativa</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleToggleEstado(parceria)}
                        >
                          {parceria.estado ? 'Desativar' : 'Ativar'}
                        </Button>
                        {parceria.tipo_parceria === 'empresa' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => criarCampanhaVales(parceria)}
                            className="text-[#376a37] border-[#376a37] hover:bg-[#376a37] hover:text-white"
                            title="Criar campanha de vales para esta parceria"
                            data-testid={`btn-nova-campanha-vales-${parceria.id}`}
                          >
                            <Gift className="h-4 w-4 mr-1" />
                            Nova campanha
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenModal(parceria)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {showAvancado && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setShowDeleteConfirm(parceria)}
                            data-testid={`btn-delete-parceria-${parceria.id}`}
                            title="Avançado: eliminar parceria (acção destrutiva)"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editando ? 'Editar Parceria' : 'Nova Parceria'}
            </DialogTitle>
            <DialogDescription>
              Configure os detalhes e regras de desconto da parceria
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Tipo de Parceria */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.tipo_parceria === 'empresa' ? 'default' : 'outline'}
                onClick={() => setFormData(prev => ({ ...prev, tipo_parceria: 'empresa' }))}
                className="flex-1"
                data-testid="btn-tipo-empresa"
              >
                Empresa / Instituição
              </Button>
              <Button
                type="button"
                variant={formData.tipo_parceria === 'perfil_beneficio' ? 'default' : 'outline'}
                onClick={() => setFormData(prev => ({ ...prev, tipo_parceria: 'perfil_beneficio' }))}
                className="flex-1"
                data-testid="btn-tipo-perfil"
              >
                Perfil de Benefício
              </Button>
            </div>

            {formData.tipo_parceria === 'perfil_beneficio' && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                Perfil de benefício interno (ex: Sénior, Família Numerosa). Gerido exclusivamente pelo Janeira, sem entidade externa.
              </div>
            )}

            {/* Informações Básicas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>{formData.tipo_parceria === 'perfil_beneficio' ? 'Nome do Perfil *' : 'Nome da Entidade *'}</Label>
                <Input
                  value={formData.nome_entidade}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome_entidade: e.target.value }))}
                  placeholder={formData.tipo_parceria === 'perfil_beneficio' ? 'Ex: Sénior, Família Numerosa' : 'Ex: Ginásio X, Farmácia Y'}
                />
              </div>

              {formData.tipo_parceria === 'empresa' && (
              <div>
                <Label>Tipo de Entidade</Label>
                <Select 
                  value={formData.tipo_entidade} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_entidade: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposEntidade.map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              {formData.tipo_parceria === 'empresa' && (
              <div>
                <Label>Modelo de Parceria</Label>
                <Select 
                  value={formData.modelo_parceria} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, modelo_parceria: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Contínuo">Contínuo</SelectItem>
                    <SelectItem value="Campanha">Campanha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>

            {/* Datas Campanha */}
            {formData.tipo_parceria === 'empresa' && formData.modelo_parceria === 'Campanha' && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <Label>Data Início Campanha</Label>
                  <Input
                    type="date"
                    value={formData.data_inicio_campanha}
                    onChange={(e) => setFormData(prev => ({ ...prev, data_inicio_campanha: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Data Fim Campanha</Label>
                  <Input
                    type="date"
                    value={formData.data_fim_campanha}
                    onChange={(e) => setFormData(prev => ({ ...prev, data_fim_campanha: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {/* Objetivo - só empresa */}
            {formData.tipo_parceria === 'empresa' && (
            <div>
              <Label>Objetivo da Parceria</Label>
              <Textarea
                value={formData.objetivo_parceria}
                onChange={(e) => setFormData(prev => ({ ...prev, objetivo_parceria: e.target.value }))}
                placeholder="Descrição do objetivo desta parceria..."
                rows={2}
              />
            </div>
            )}

            {/* Configurações - apenas para empresa (perfil_beneficio não gera vales) */}
            {formData.tipo_parceria === 'empresa' && (
            <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dias de Validade Padrão</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.dias_validade_padrao}
                  onChange={(e) => setFormData(prev => ({ ...prev, dias_validade_padrao: parseInt(e.target.value) || 8 }))}
                />
              </div>

              <div>
                <Label>Regra de Acumulação</Label>
                <Select 
                  value={formData.regra_acumulacao} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, regra_acumulacao: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Não acumula">Não acumula</SelectItem>
                    <SelectItem value="Limite">Limite</SelectItem>
                    <SelectItem value="Sempre">Sempre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.regra_acumulacao === 'Limite' && (
              <div>
                <Label>Limite Máximo de Vales</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.limite_maximo_vales}
                  onChange={(e) => setFormData(prev => ({ ...prev, limite_maximo_vales: e.target.value }))}
                />
              </div>
            )}

            {/* Switches */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Bloqueia se Vale Ativo</Label>
                  <p className="text-xs text-muted-foreground">Impede novo vale se cliente já tiver um ativo</p>
                </div>
                <Switch
                  checked={formData.bloqueia_se_vale_ativo}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, bloqueia_se_vale_ativo: v }))}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div>
                  <Label className="text-amber-800">Limite 1 Vale por Dia</Label>
                  <p className="text-xs text-amber-700">Cada cartão só pode gerar 1 vale por dia desta parceria</p>
                </div>
                <Switch
                  checked={formData.limite_um_vale_por_dia}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, limite_um_vale_por_dia: v }))}
                  data-testid="switch-limite-vale-dia"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Alerta ao Colaborador</Label>
                  <p className="text-xs text-muted-foreground">Mostra alertas sobre vales ativos</p>
                </div>
                <Switch
                  checked={formData.alerta_colaborador}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, alerta_colaborador: v }))}
                />
              </div>
            </div>
            </>
            )}

            {/* Datas Parceria */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início Parceria *</Label>
                <Input
                  type="date"
                  value={formData.data_inicio_parceria}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_inicio_parceria: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data Fim Parceria (opcional)</Label>
                <Input
                  type="date"
                  value={formData.data_fim_parceria}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_fim_parceria: e.target.value }))}
                />
              </div>
            </div>

            {/* Configuração de Partilha de Custos - só para empresa */}
            {formData.tipo_parceria === 'empresa' && (
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Partilha de Custos
              </h4>
              <p className="text-xs text-muted-foreground">
                Configure quem suporta os custos dos descontos dados em cada direção
              </p>
              
              <div>
                <Label>Tipo de Partilha</Label>
                <Select 
                  value={formData.config_custos?.tipo_custo || 'unilateral_janeira'} 
                  onValueChange={(v) => {
                    let newConfig = { ...formData.config_custos, tipo_custo: v };
                    // Ajustar defaults baseado no tipo
                    if (v === 'unilateral_janeira') {
                      newConfig = {
                        ...newConfig,
                        janeira_parceiro_percentagem_janeira: 100,
                        janeira_parceiro_percentagem_parceiro: 0,
                        parceiro_janeira_percentagem_janeira: 100,
                        parceiro_janeira_percentagem_parceiro: 0
                      };
                    } else if (v === 'unilateral_parceiro') {
                      newConfig = {
                        ...newConfig,
                        janeira_parceiro_percentagem_janeira: 0,
                        janeira_parceiro_percentagem_parceiro: 100,
                        parceiro_janeira_percentagem_janeira: 0,
                        parceiro_janeira_percentagem_parceiro: 100
                      };
                    }
                    setFormData(prev => ({ ...prev, config_custos: newConfig }));
                  }}
                >
                  <SelectTrigger data-testid="select-tipo-custo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unilateral_janeira">Janeira suporta 100%</SelectItem>
                    <SelectItem value="unilateral_parceiro">Parceiro suporta 100%</SelectItem>
                    <SelectItem value="bilateral_percentagem">Partilha por Percentagem</SelectItem>
                    <SelectItem value="bilateral_fixo">Partilha por Valor Fixo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Configuração detalhada para partilha por percentagem */}
              {formData.config_custos?.tipo_custo === 'bilateral_percentagem' && (
                <div className="space-y-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium text-emerald-800">
                      Janeira → Parceiro (desconto dado no parceiro)
                    </Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label className="text-xs">Janeira paga (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.config_custos?.janeira_parceiro_percentagem_janeira || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setFormData(prev => ({
                              ...prev,
                              config_custos: {
                                ...prev.config_custos,
                                janeira_parceiro_percentagem_janeira: val,
                                janeira_parceiro_percentagem_parceiro: 100 - val
                              }
                            }));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Parceiro paga (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.config_custos?.janeira_parceiro_percentagem_parceiro || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setFormData(prev => ({
                              ...prev,
                              config_custos: {
                                ...prev.config_custos,
                                janeira_parceiro_percentagem_parceiro: val,
                                janeira_parceiro_percentagem_janeira: 100 - val
                              }
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {formData.ativar_parceiro_janeira && (
                    <div>
                      <Label className="text-sm font-medium text-emerald-800">
                        Parceiro → Janeira (desconto dado no Janeira)
                      </Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <Label className="text-xs">Janeira paga (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.config_custos?.parceiro_janeira_percentagem_janeira || 0}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setFormData(prev => ({
                                ...prev,
                                config_custos: {
                                  ...prev.config_custos,
                                  parceiro_janeira_percentagem_janeira: val,
                                  parceiro_janeira_percentagem_parceiro: 100 - val
                                }
                              }));
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Parceiro paga (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.config_custos?.parceiro_janeira_percentagem_parceiro || 0}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setFormData(prev => ({
                                ...prev,
                                config_custos: {
                                  ...prev.config_custos,
                                  parceiro_janeira_percentagem_parceiro: val,
                                  parceiro_janeira_percentagem_janeira: 100 - val
                                }
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Configuração detalhada para partilha por valor fixo */}
              {formData.config_custos?.tipo_custo === 'bilateral_fixo' && (
                <div className="space-y-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium text-purple-800">
                      Janeira → Parceiro (desconto dado no parceiro)
                    </Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label className="text-xs">Janeira paga (€/transação)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.config_custos?.janeira_parceiro_valor_fixo_janeira || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              config_custos: {
                                ...prev.config_custos,
                                janeira_parceiro_valor_fixo_janeira: parseFloat(e.target.value) || null
                              }
                            }));
                          }}
                          placeholder="Ex: 0.18"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Parceiro paga (€/transação)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.config_custos?.janeira_parceiro_valor_fixo_parceiro || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              config_custos: {
                                ...prev.config_custos,
                                janeira_parceiro_valor_fixo_parceiro: parseFloat(e.target.value) || null
                              }
                            }));
                          }}
                          placeholder="Ex: 0.02"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {formData.ativar_parceiro_janeira && (
                    <div>
                      <Label className="text-sm font-medium text-purple-800">
                        Parceiro → Janeira (desconto dado no Janeira)
                      </Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <Label className="text-xs">Janeira paga (€/transação)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.config_custos?.parceiro_janeira_valor_fixo_janeira || ''}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                config_custos: {
                                  ...prev.config_custos,
                                  parceiro_janeira_valor_fixo_janeira: parseFloat(e.target.value) || null
                                }
                              }));
                            }}
                            placeholder="Ex: 0.02"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Parceiro paga (€/transação)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.config_custos?.parceiro_janeira_valor_fixo_parceiro || ''}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                config_custos: {
                                  ...prev.config_custos,
                                  parceiro_janeira_valor_fixo_parceiro: parseFloat(e.target.value) || null
                                }
                              }));
                            }}
                            placeholder="Ex: 0.18"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-purple-600">
                    O valor fixo será aplicado por transação. Se o desconto for maior que a soma dos valores fixos, a diferença será absorvida proporcionalmente.
                  </p>
                </div>
              )}

              {/* Preview da configuração */}
              <div className="p-3 bg-gray-100 rounded-lg text-sm">
                <strong>Resumo da partilha:</strong>
                <ul className="mt-1 text-xs text-muted-foreground">
                  {formData.config_custos?.tipo_custo === 'unilateral_janeira' && (
                    <li>• Janeira suporta 100% de todos os descontos</li>
                  )}
                  {formData.config_custos?.tipo_custo === 'unilateral_parceiro' && (
                    <li>• Parceiro suporta 100% de todos os descontos</li>
                  )}
                  {formData.config_custos?.tipo_custo === 'bilateral_percentagem' && (
                    <>
                      <li>• J→P: Janeira {formData.config_custos?.janeira_parceiro_percentagem_janeira || 0}% / Parceiro {formData.config_custos?.janeira_parceiro_percentagem_parceiro || 0}%</li>
                      {formData.ativar_parceiro_janeira && (
                        <li>• P→J: Janeira {formData.config_custos?.parceiro_janeira_percentagem_janeira || 0}% / Parceiro {formData.config_custos?.parceiro_janeira_percentagem_parceiro || 0}%</li>
                      )}
                    </>
                  )}
                  {formData.config_custos?.tipo_custo === 'bilateral_fixo' && (
                    <>
                      <li>• J→P: Janeira €{formData.config_custos?.janeira_parceiro_valor_fixo_janeira || 0} / Parceiro €{formData.config_custos?.janeira_parceiro_valor_fixo_parceiro || 0} por transação</li>
                      {formData.ativar_parceiro_janeira && (
                        <li>• P→J: Janeira €{formData.config_custos?.parceiro_janeira_valor_fixo_janeira || 0} / Parceiro €{formData.config_custos?.parceiro_janeira_valor_fixo_parceiro || 0} por transação</li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
            )}

            {/* Regras de Desconto - Bidirecionais */}
            <div className="border-t pt-4 space-y-6">
              <h4 className="font-medium">Regras de Desconto</h4>
              
              {/* === DIREÇÃO 1: Regras de Desconto === */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <h5 className="font-medium text-blue-800 text-sm">
                  {formData.tipo_parceria === 'perfil_beneficio' 
                    ? `Desconto no Janeira`
                    : `Janeira → ${formData.nome_entidade || 'Parceiro'}`}
                </h5>
                <p className="text-xs text-blue-600">
                  {formData.tipo_parceria === 'perfil_beneficio'
                    ? 'Cliente compra no Janeira, desconto aplicado automaticamente'
                    : 'Cliente compra no Janeira, gera vale para usar no parceiro'}
                </p>
                
                {formData.regras_desconto.length > 0 && (
                  <div className="space-y-2">
                    {formData.regras_desconto.map((regra, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="text-sm">
                          <span>Compras ≥ <span className="font-bold">€{regra.valor_minimo_compra}</span></span>
                          <span className="mx-1">→</span>
                          <span className="font-bold text-blue-700">
                            {regra.tipo_beneficio === 'Valor Fixo' ? `€${regra.valor_beneficio}` 
                              : regra.tipo_beneficio === '€/Litro' ? `€${regra.valor_beneficio}/L${regra.limite_litros ? ` (máx ${regra.limite_litros}L)` : ''}`
                              : `${regra.valor_beneficio}%`}
                          </span>
                          <span className="text-muted-foreground ml-1">de desconto</span>
                          {regra.valor_minimo_utilizacao > 0 && (
                            <span className="ml-1 text-xs text-orange-600">(usar em compras ≥ €{regra.valor_minimo_utilizacao})</span>
                          )}
                          {(regra.data_inicio || regra.data_fim) && (
                            <span className="ml-1 text-xs text-purple-600">
                              | {regra.data_inicio || '...'} a {regra.data_fim || '...'}
                            </span>
                          )}
                          {!regra.data_inicio && !regra.data_fim && (
                            <span className="ml-1 text-xs text-muted-foreground">| Sempre ativa</span>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveRegra('regras_desconto', index)}>
                          <Trash2 className="h-3 w-3 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Compra Mín (€)</Label>
                    <Input type="number" step="0.01" value={novaRegraIda.valor_minimo_compra}
                      onChange={(e) => setNovaRegraIda(p => ({ ...p, valor_minimo_compra: e.target.value }))} placeholder="40" />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={novaRegraIda.tipo_beneficio} onValueChange={(v) => setNovaRegraIda(p => ({ ...p, tipo_beneficio: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Valor Fixo">€ Fixo</SelectItem>
                        <SelectItem value="Percentagem">%</SelectItem>
                        {formData.tipo_parceria === 'empresa' && (
                          <SelectItem value="€/Litro">€/Litro</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{novaRegraIda.tipo_beneficio === '€/Litro' ? '€/L' : 'Valor'}</Label>
                    <Input type="number" step="0.01" value={novaRegraIda.valor_beneficio}
                      onChange={(e) => setNovaRegraIda(p => ({ ...p, valor_beneficio: e.target.value }))} placeholder={novaRegraIda.tipo_beneficio === '€/Litro' ? '0.20' : '3'} />
                  </div>
                  {novaRegraIda.tipo_beneficio === '€/Litro' && (
                    <div>
                      <Label className="text-xs">Máx Litros</Label>
                      <Input type="number" value={novaRegraIda.limite_litros}
                        onChange={(e) => setNovaRegraIda(p => ({ ...p, limite_litros: e.target.value }))} placeholder="20" />
                    </div>
                  )}
                  {formData.tipo_parceria === 'empresa' && (
                  <div>
                    <Label className="text-xs">Mín. p/ usar (€)</Label>
                    <Input type="number" step="0.01" value={novaRegraIda.valor_minimo_utilizacao}
                      onChange={(e) => setNovaRegraIda(p => ({ ...p, valor_minimo_utilizacao: e.target.value }))} placeholder="20" />
                  </div>
                  )}
                </div>
                {novaRegraIda.valor_minimo_compra && novaRegraIda.valor_beneficio && (
                  <div className="p-2 bg-blue-100 rounded text-xs text-blue-800 font-medium">
                    Compras ≥ €{novaRegraIda.valor_minimo_compra} no Janeira → desconto de {
                      novaRegraIda.tipo_beneficio === 'Valor Fixo' ? `€${novaRegraIda.valor_beneficio}` 
                      : novaRegraIda.tipo_beneficio === '€/Litro' ? `€${novaRegraIda.valor_beneficio}/L${novaRegraIda.limite_litros ? ` (máx ${novaRegraIda.limite_litros}L)` : ''}`
                      : `${novaRegraIda.valor_beneficio}%`
                    } {formData.tipo_parceria === 'perfil_beneficio' ? 'aplicado no Janeira' : `no ${formData.nome_entidade || 'parceiro'}`}
                    {novaRegraIda.valor_minimo_utilizacao && formData.tipo_parceria === 'empresa' ? ` (válido em compras ≥ €${novaRegraIda.valor_minimo_utilizacao})` : ''}
                    {novaRegraIda.data_inicio || novaRegraIda.data_fim ? ` | Período: ${novaRegraIda.data_inicio || '...'} a ${novaRegraIda.data_fim || '...'}` : ' | Sempre ativa'}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Data Início (opcional)</Label>
                    <Input type="date" value={novaRegraIda.data_inicio}
                      onChange={(e) => setNovaRegraIda(p => ({ ...p, data_inicio: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Data Fim (opcional)</Label>
                    <Input type="date" value={novaRegraIda.data_fim}
                      onChange={(e) => setNovaRegraIda(p => ({ ...p, data_fim: e.target.value }))} />
                  </div>
                </div>
                <Button onClick={() => handleAddRegra('ida')} size="sm" variant="outline" className="w-full">
                  <Plus className="h-3 w-3 mr-1" /> Adicionar Regra
                </Button>
              </div>

              {/* === DIREÇÃO 2: Parceiro → Janeira (Opcional, só empresa) === */}
              {formData.tipo_parceria === 'empresa' && (
              <div className={`p-4 border rounded-lg space-y-3 ${formData.ativar_parceiro_janeira ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className={`font-medium text-sm ${formData.ativar_parceiro_janeira ? 'text-amber-800' : 'text-gray-400'}`}>
                      {formData.nome_entidade || 'Parceiro'} → Janeira
                    </h5>
                    <p className={`text-xs ${formData.ativar_parceiro_janeira ? 'text-amber-600' : 'text-gray-400'}`}>
                      Cliente compra no parceiro, usa o vale no Janeira
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground">Ativar</Label>
                    <Switch
                      checked={formData.ativar_parceiro_janeira || false}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, ativar_parceiro_janeira: checked }))}
                    />
                  </div>
                </div>
                
                {formData.ativar_parceiro_janeira && (
                  <>
                    {formData.regras_parceiro_janeira.length > 0 && (
                      <div className="space-y-2">
                        {formData.regras_parceiro_janeira.map((regra, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div className="text-sm">
                              <span>Compras ≥ <span className="font-bold">€{regra.valor_minimo_compra}</span></span>
                              <span className="mx-1">→</span>
                              <span className="font-bold text-amber-700">
                                {regra.tipo_beneficio === 'Valor Fixo' ? `€${regra.valor_beneficio}` 
                                  : regra.tipo_beneficio === '€/Litro' ? `€${regra.valor_beneficio}/L${regra.limite_litros ? ` (máx ${regra.limite_litros}L)` : ''}`
                                  : `${regra.valor_beneficio}%`}
                              </span>
                              <span className="text-muted-foreground ml-1">de desconto</span>
                              {regra.valor_minimo_utilizacao > 0 && (
                                <span className="ml-1 text-xs text-orange-600">(usar em compras ≥ €{regra.valor_minimo_utilizacao})</span>
                              )}
                              {(regra.data_inicio || regra.data_fim) && (
                                <span className="ml-1 text-xs text-purple-600">
                                  | {regra.data_inicio || '...'} a {regra.data_fim || '...'}
                                </span>
                              )}
                              {!regra.data_inicio && !regra.data_fim && (
                                <span className="ml-1 text-xs text-muted-foreground">| Sempre ativa</span>
                              )}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveRegra('regras_parceiro_janeira', index)}>
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Compra Mín (€)</Label>
                        <Input type="number" step="0.01" value={novaRegraVolta.valor_minimo_compra}
                          onChange={(e) => setNovaRegraVolta(p => ({ ...p, valor_minimo_compra: e.target.value }))} placeholder="20" />
                      </div>
                      <div>
                        <Label className="text-xs">Tipo</Label>
                        <Select value={novaRegraVolta.tipo_beneficio} onValueChange={(v) => setNovaRegraVolta(p => ({ ...p, tipo_beneficio: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Valor Fixo">€ Fixo</SelectItem>
                            <SelectItem value="Percentagem">%</SelectItem>
                            <SelectItem value="€/Litro">€/Litro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">{novaRegraVolta.tipo_beneficio === '€/Litro' ? '€/L' : 'Valor'}</Label>
                        <Input type="number" step="0.01" value={novaRegraVolta.valor_beneficio}
                          onChange={(e) => setNovaRegraVolta(p => ({ ...p, valor_beneficio: e.target.value }))} placeholder={novaRegraVolta.tipo_beneficio === '€/Litro' ? '0.20' : '3'} />
                      </div>
                      {novaRegraVolta.tipo_beneficio === '€/Litro' && (
                        <div>
                          <Label className="text-xs">Máx Litros</Label>
                          <Input type="number" value={novaRegraVolta.limite_litros}
                            onChange={(e) => setNovaRegraVolta(p => ({ ...p, limite_litros: e.target.value }))} placeholder="20" />
                        </div>
                      )}
                      <div>
                        <Label className="text-xs">Mín. p/ usar (€)</Label>
                        <Input type="number" step="0.01" value={novaRegraVolta.valor_minimo_utilizacao}
                          onChange={(e) => setNovaRegraVolta(p => ({ ...p, valor_minimo_utilizacao: e.target.value }))} placeholder="20" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Data Início (opcional)</Label>
                        <Input type="date" value={novaRegraVolta.data_inicio}
                          onChange={(e) => setNovaRegraVolta(p => ({ ...p, data_inicio: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs">Data Fim (opcional)</Label>
                        <Input type="date" value={novaRegraVolta.data_fim}
                          onChange={(e) => setNovaRegraVolta(p => ({ ...p, data_fim: e.target.value }))} />
                      </div>
                    </div>
                    {novaRegraVolta.valor_minimo_compra && novaRegraVolta.valor_beneficio && (
                      <div className="p-2 bg-amber-100 rounded text-xs text-amber-800 font-medium">
                        Compras ≥ €{novaRegraVolta.valor_minimo_compra} no {formData.nome_entidade || 'parceiro'} → desconto de {
                          novaRegraVolta.tipo_beneficio === 'Valor Fixo' ? `€${novaRegraVolta.valor_beneficio}` 
                          : novaRegraVolta.tipo_beneficio === '€/Litro' ? `€${novaRegraVolta.valor_beneficio}/L${novaRegraVolta.limite_litros ? ` (máx ${novaRegraVolta.limite_litros}L)` : ''}`
                          : `${novaRegraVolta.valor_beneficio}%`
                        } no Janeira
                        {novaRegraVolta.valor_minimo_utilizacao ? ` (válido em compras ≥ €${novaRegraVolta.valor_minimo_utilizacao})` : ''}
                        {novaRegraVolta.data_inicio || novaRegraVolta.data_fim ? ` | Período: ${novaRegraVolta.data_inicio || '...'} a ${novaRegraVolta.data_fim || '...'}` : ' | Sempre ativa'}
                      </div>
                    )}
                    <Button onClick={() => handleAddRegra('volta')} size="sm" variant="outline" className="w-full">
                      <Plus className="h-3 w-3 mr-1" /> Adicionar Regra
                    </Button>
                  </>
                )}
              </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              {editando ? 'Guardar' : 'Criar Parceria'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de Eliminação */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Parceria</DialogTitle>
            <DialogDescription>
              Tem a certeza que deseja eliminar a parceria "{showDeleteConfirm?.nome_entidade}"?
              Esta ação também eliminará todos os vales e utilizadores associados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestaoParcerias;
